import React, { useState } from "react";
import { MoreHorizontal, Pause, Play, Trash2, RotateCcw, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

/** Dropdown of actions for a tenant row + confirm modal for destructive ops.
 *  Keeping this as a separate component means the table row stays clean. */
export default function SATenantActions({ tenant, onAction }) {
    const [open, setOpen] = useState(false);
    const [modal, setModal] = useState(null); // {kind: 'suspend'|'delete', ...}
    const [reason, setReason] = useState("");
    const [confirmSlug, setConfirmSlug] = useState("");
    const [busy, setBusy] = useState(false);

    const isDeleted = !!tenant.deleted_at;
    const isSuspended = tenant.status === "suspended" && !isDeleted;

    const close = () => {
        setOpen(false); setModal(null); setReason(""); setConfirmSlug("");
    };

    const run = async (action, payload) => {
        setBusy(true);
        try {
            await onAction(action, payload);
            close();
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="relative inline-block text-left">
            <button
                data-testid={`sa-tenant-actions-${tenant.slug || tenant.id}`}
                onClick={() => setOpen((o) => !o)}
                className="rounded-md p-1.5 text-white/55 transition-colors hover:bg-white/5 hover:text-white"
            >
                <MoreHorizontal size={14} />
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-10" onClick={close} />
                    <div
                        className="absolute right-0 z-20 mt-1 w-44 rounded-md border border-white/10 bg-[#0b1226] shadow-xl"
                        data-testid="sa-tenant-menu"
                    >
                        <MenuItem
                            icon={ExternalLink}
                            label="View"
                            to={`/superadmin/tenants/${tenant.id}`}
                            onClose={close}
                            testId="sa-action-view"
                        />
                        {isDeleted ? (
                            <MenuItem
                                icon={RotateCcw}
                                label="Restore"
                                onClick={() => { setModal({ kind: "restore" }); setOpen(false); }}
                                testId="sa-action-restore"
                            />
                        ) : isSuspended ? (
                            <MenuItem
                                icon={Play}
                                label="Activate"
                                onClick={() => { setModal({ kind: "activate" }); setOpen(false); }}
                                testId="sa-action-activate"
                            />
                        ) : (
                            <MenuItem
                                icon={Pause}
                                label="Suspend"
                                onClick={() => { setModal({ kind: "suspend" }); setOpen(false); }}
                                testId="sa-action-suspend"
                            />
                        )}
                        {!isDeleted && (
                            <MenuItem
                                icon={Trash2}
                                label="Delete"
                                danger
                                onClick={() => { setModal({ kind: "delete" }); setOpen(false); }}
                                testId="sa-action-delete"
                            />
                        )}
                    </div>
                </>
            )}

            {modal && (
                <Modal onClose={close}>
                    <div className="text-[15px] font-semibold">
                        {modal.kind === "suspend" && `Suspend ${tenant.name}?`}
                        {modal.kind === "activate" && `Activate ${tenant.name}?`}
                        {modal.kind === "restore" && `Restore ${tenant.name}?`}
                        {modal.kind === "delete" && `Delete ${tenant.name}?`}
                    </div>
                    <div className="mt-2 text-[12px] text-white/65">
                        {modal.kind === "suspend" && (
                            <>
                                Users in this workspace won't be able to log in. Running jobs will be
                                paused. Existing agents keep heartbeating but won't claim new work.
                            </>
                        )}
                        {modal.kind === "activate" && (
                            <>
                                Logins + paused jobs resume immediately.
                            </>
                        )}
                        {modal.kind === "restore" && (
                            <>
                                Cancels the pending hard-purge and re-activates the workspace.
                            </>
                        )}
                        {modal.kind === "delete" && (
                            <>
                                Marks the workspace for <strong className="text-red-300">permanent deletion
                                in 30 days</strong>. Users can't log in during the grace period. You can Restore
                                any time before then. Type the slug <code className="rounded bg-white/10 px-1 py-px text-[11px] text-[#00C2FF]">{tenant.slug}</code> to confirm.
                            </>
                        )}
                    </div>

                    <label className="mt-3 block text-[10px] uppercase tracking-wider text-white/50">
                        Reason (optional)
                    </label>
                    <input
                        data-testid="sa-action-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. non-payment, security review…"
                        className="mt-1 h-8 w-full rounded-md border border-white/10 bg-white/[0.03] px-2 text-[12.5px] text-white focus:border-[#00C2FF]/50 focus:outline-none"
                    />

                    {modal.kind === "delete" && (
                        <>
                            <label className="mt-3 block text-[10px] uppercase tracking-wider text-red-300/80">
                                Type slug to confirm
                            </label>
                            <input
                                data-testid="sa-action-confirm-slug"
                                value={confirmSlug}
                                onChange={(e) => setConfirmSlug(e.target.value)}
                                placeholder={tenant.slug || "tenant-slug"}
                                className="mt-1 h-8 w-full rounded-md border border-red-400/30 bg-red-400/5 px-2 text-[12.5px] text-white focus:border-red-400/60 focus:outline-none"
                            />
                        </>
                    )}

                    <div className="mt-4 flex items-center justify-end gap-2">
                        <button
                            onClick={close}
                            disabled={busy}
                            className="rounded-md border border-white/10 px-3 py-1.5 text-[12px] text-white/75 hover:bg-white/5"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid="sa-action-confirm"
                            disabled={busy || (modal.kind === "delete" && confirmSlug !== tenant.slug)}
                            onClick={() => {
                                if (modal.kind === "delete") {
                                    run("delete", { reason: reason || null, confirm_slug: confirmSlug });
                                } else {
                                    run(modal.kind, { reason: reason || null });
                                }
                            }}
                            className={[
                                "rounded-md px-3 py-1.5 text-[12px] font-semibold transition-colors disabled:opacity-40",
                                modal.kind === "delete"
                                    ? "bg-red-500 text-white hover:bg-red-400"
                                    : "bg-[#004AAD] text-white hover:bg-[#005ed0]",
                            ].join(" ")}
                        >
                            {busy ? "Working…" : modal.kind === "delete" ? "Delete" : "Confirm"}
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
}

function MenuItem({ icon: Icon, label, onClick, to, onClose, danger, testId }) {
    const cls = [
        "flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] transition-colors",
        danger ? "text-red-300 hover:bg-red-500/10" : "text-white/80 hover:bg-white/5",
    ].join(" ");
    if (to) {
        return (
            <Link to={to} data-testid={testId} className={cls} onClick={onClose}>
                <Icon size={12} /> {label}
            </Link>
        );
    }
    return (
        <button data-testid={testId} className={cls} onClick={onClick}>
            <Icon size={12} /> {label}
        </button>
    );
}

function Modal({ children, onClose }) {
    // Register Escape-to-close on mount. Using window here instead of the
    // dialog root because browsers don't route keydown to non-focused divs.
    React.useEffect(() => {
        const handler = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
            onClick={onClose}
            data-testid="sa-action-modal-backdrop"
        >
            <div
                className="w-full max-w-md rounded-xl border border-white/10 bg-[#0b1226] p-5 shadow-2xl"
                data-testid="sa-action-modal"
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </div>
    );
}
